import { Button, DrawerCard, DrawerTitle, ModalAlert, Pill, Spinner } from "@mixitone/components";
import { DrawerContextLink } from "components/common/DrawerContextLink";
import { ApplicationView } from "@mixitone/mvc";
import { capitalize, formatDate, formatMembershipPeriodLabel } from "@mixitone/util";
import ClubPlayerFormController from "components/Forms/ClubPlayerFormController";
import Form from "components/Forms/Form";
import { ModalFormFieldset } from "components/Forms/FormRenderers/ModalFormRenderer";
import RatingTooltip from "components/common/RatingTooltip";
import { SectionTabs } from "components/common/SectionTabs";
import CrossIcon from "components/icons/CrossIcon";
import TickIcon from "components/icons/TickIcon";
import MemberNoteField from "components/Pages/Club/ClubPlayer/MemberNoteField";
import { attentionReasonLabel, getMemberBookAttentionReasons } from "lib/memberBookAttention";
import { useState } from "react";
import { Link } from "router";

const MEMBER_PROFILE_TABS = [
  { id: "profile", label: "Profile" },
  { id: "activity", label: "Activity" },
  { id: "notes", label: "Notes" },
  { id: "membership", label: "Membership" },
] as const;

type TabId = (typeof MEMBER_PROFILE_TABS)[number]["id"];

interface Props {
  clubId: string;
  id: string;
  attributes?: Record<string, never>;
}

const MemberProfileTitle = ApplicationView(() => {
  const controller = ClubPlayerFormController.use();
  const { clubId } = controller.props;
  const name = controller.state.record.name;

  return (
    <DrawerTitle contextLink={<DrawerContextLink context="members" params={{ clubId }} />}>
      {name?.trim() || "Member"}
    </DrawerTitle>
  );
});

const MemberProfileSummary = ApplicationView(() => {
  const controller = ClubPlayerFormController.use();
  const { record, club, sessionCount, lastSession, clubDocuments } = controller.state;
  const groupName = record.group?.name ?? "—";
  const lastPlayed =
    lastSession != null
      ? {
          nights_played: sessionCount,
          date_of_last_night_played:
            typeof lastSession === "string" ? lastSession : lastSession.toISOString(),
        }
      : { nights_played: sessionCount, date_of_last_night_played: "" };
  const attentionReasons = getMemberBookAttentionReasons(record, club, lastPlayed, [...clubDocuments]);

  return (
    <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-gray-900">{record.name}</span>
        {club.memberTypes.length > 1 && (
          <span className="text-gray-600">{capitalize(record.derivedMemberType)}</span>
        )}
        <span className="text-gray-500">· {groupName}</span>
        <span className="inline-flex items-center gap-1">
          {record.active ? (
            <TickIcon className="w-[14px] stroke-teal-600" />
          ) : (
            <CrossIcon className="w-[14px] fill-red-800" />
          )}
          {record.active ? "Active" : "Inactive"}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-gray-600">
        <span>{sessionCount} sessions</span>
        <span>Last played: {lastSession ? formatDate(lastSession) : "Never"}</span>
        <span className="inline-flex items-center gap-1">
          Rating: {record.ratingOrDefault}
          <RatingTooltip />
        </span>
      </div>
      {attentionReasons.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {attentionReasons.map((reason) => (
            <Pill key={reason} color="yellow" className="text-xs">
              {attentionReasonLabel(reason)}
            </Pill>
          ))}
        </div>
      )}
    </div>
  );
});

const ProfileFieldSet = ApplicationView(() => {
  const controller = ClubPlayerFormController.use();
  return (
    <DrawerCard>
      <ModalFormFieldset fields={controller.fieldsForGroup("profile")} />
    </DrawerCard>
  );
});

const MemberProfileActivityTab = ApplicationView(() => {
  const controller = ClubPlayerFormController.use();
  const { record, sessionCount, lastSession } = controller.state;

  return (
    <DrawerCard>
      <dl className="space-y-3 text-sm">
        <div className="flex justify-between">
          <dt className="font-medium text-gray-700">Sessions played</dt>
          <dd>{sessionCount}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="font-medium text-gray-700">Last session</dt>
          <dd>{lastSession ? formatDate(lastSession) : "Never"}</dd>
        </div>
        <div className="flex justify-between items-center">
          <dt className="font-medium text-gray-700 inline-flex items-center gap-1">
            Rating <RatingTooltip />
          </dt>
          <dd>{record.ratingOrDefault}</dd>
        </div>
        {record.latest_rating_date && (
          <div className="flex justify-between">
            <dt className="font-medium text-gray-700">Rating updated</dt>
            <dd>{formatDate(record.latest_rating_date)}</dd>
          </div>
        )}
      </dl>
    </DrawerCard>
  );
});

const MemberProfileNotesTab = ApplicationView(() => {
  const controller = ClubPlayerFormController.use();
  const { club, memberNoteText, notesUpdating } = controller.state;

  return (
    <DrawerCard>
      <MemberNoteField
        value={memberNoteText}
        placeholder="Add a note about this member…"
        readOnly={!club.hasManagePermission()}
        saving={notesUpdating}
        onSave={(text) => controller.actionSaveMemberNote(text)}
      />
    </DrawerCard>
  );
});

const MemberProfileMembershipTab = ApplicationView(() => {
  const controller = ClubPlayerFormController.use();
  const { clubId } = controller.props;
  const { record, club, membershipUpdating, membershipRequirements } = controller.state;
  const membershipFields = controller.fieldsForGroup("membership");
  const membershipsEnabled = controller.membershipsEnabled;
  const isCurrentMember = record.isCurrentMember;
  const membershipStartedAt = record.membershipStartedAt;
  const membershipPeriods = controller.membershipPeriodsForDisplay;
  const membershipBusy = controller.state.saving || membershipUpdating;
  const canManageMembership = club.hasManagePermission() && !membershipBusy;

  return (
    <DrawerCard>
      {membershipsEnabled && (
        <div className="mb-6 space-y-4">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Status</h3>
            <p className="mt-1 text-sm text-gray-600">
              {isCurrentMember && membershipStartedAt
                ? `Current member since ${formatDate(membershipStartedAt)}`
                : "Not currently a member"}
            </p>
          </div>

          {club.hasManagePermission() && (
            <div className="flex flex-wrap gap-2">
              {!isCurrentMember && (
                <Button
                  type="button"
                  kind="primary"
                  disabled={!canManageMembership}
                  spinner={membershipUpdating}
                  onClick={() => controller.actionStartMembership()}
                >
                  Start membership
                </Button>
              )}
              {isCurrentMember && (
                <Button
                  type="button"
                  kind="danger"
                  disabled={!canManageMembership}
                  spinner={membershipUpdating}
                  onClick={() => controller.actionEndMembership()}
                >
                  End membership
                </Button>
              )}
            </div>
          )}

          {!isCurrentMember && membershipRequirements.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <p className="font-medium">Required before starting membership</p>
              <ul className="mt-1 list-disc pl-5">
                {membershipRequirements.map((reason) => (
                  <li key={`${reason.field}-${reason.message}`}>{reason.message}</li>
                ))}
              </ul>
            </div>
          )}

          {membershipPeriods.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-900">History</h3>
              <ul className="mt-2 space-y-1 text-sm text-gray-600">
                {membershipPeriods.map((period) => (
                  <li key={period.id}>{formatMembershipPeriodLabel(period.started_at, period.ended_at)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {membershipFields.length === 0 && !membershipsEnabled ? (
        <p className="text-sm text-gray-500">
          No{" "}
          <Link
            to="/settings/clubs/:clubId/players"
            params={{ clubId }}
            className="text-blue-500 hover:underline"
            viewTransition
          >
            membership settings
          </Link>{" "}
          for this club.
        </p>
      ) : membershipFields.length > 0 ? (
        <ModalFormFieldset fields={membershipFields} />
      ) : null}
    </DrawerCard>
  );
});

const MemberProfileFormActions = ApplicationView(() => {
  const controller = ClubPlayerFormController.use();
  const { saving, club } = controller.state;
  const disabled = saving || !club.hasManagePermission();

  if (!club.hasManagePermission()) return null;

  return (
    <div className="mt-4 flex items-center justify-end gap-2">
      {controller.canDelete && (
        <Button type="button" kind="danger" disabled={disabled} onClick={() => controller.actionDelete()}>
          Delete
        </Button>
      )}
      <Button type="submit" disabled={disabled} spinner={saving}>
        Save
      </Button>
    </div>
  );
});

const EndMembershipModal = ApplicationView(() => {
  const controller = ClubPlayerFormController.use();
  const { showEndMembershipModal, membershipUpdating, record } = controller.state;

  if (!showEndMembershipModal) return null;

  return (
    <ModalAlert
      title="End membership"
      message={`End membership for ${record.name}? They will remain in the member book as a casual player.`}
      onConfirm={() => controller.actionConfirmEndMembership()}
      onCancel={() => controller.actionCancelEndMembership()}
      confirmText="End membership"
      cancelText="Cancel"
      loading={membershipUpdating}
    />
  );
});

const ClubPlayerMemberProfile: React.FC<Props> = () => {
  const controller = ClubPlayerFormController.use();
  const [activeTabId, setActiveTabId] = useState<TabId>("profile");
  const { loading } = controller.state;

  if (loading) return <Spinner size={64} />;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <MemberProfileTitle />
      <MemberProfileSummary />

      <div className="-mx-4 mb-4">
        <SectionTabs
          tabs={[...MEMBER_PROFILE_TABS]}
          activeTabId={activeTabId}
          onChange={(tabId) => {
            const nextTab = tabId as TabId;
            setActiveTabId(nextTab);
            if (nextTab === "membership") {
              void controller.actionLoadMembershipRequirements();
            }
          }}
        />
      </div>

      <Form name="player">
        {activeTabId === "profile" && <ProfileFieldSet />}
        {activeTabId === "activity" && <MemberProfileActivityTab />}
        {activeTabId === "notes" && <MemberProfileNotesTab />}
        {activeTabId === "membership" && <MemberProfileMembershipTab />}
        <MemberProfileFormActions />
      </Form>

      <EndMembershipModal />
    </div>
  );
};

export default ClubPlayerFormController.scope(ApplicationView(ClubPlayerMemberProfile));
